import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource, ridKey } from '../../../shared/model/keys'
import { saleSearchSubmitted } from './commands'
import type { DeliveryOffer } from './commonSearch'
import type {
  CartBase,
  CartEdit,
  PaymentKind,
  PendingLookup,
  ReceiptRetryDraft,
  ResolveDraft,
  SaleIntentState,
  SaleSeatIntent,
} from './types'

/**
 * Намерение окна продажи (срок `screen`): выбор способа оплаты, поиск подбора, формы окна оплаты, черновик ручной сверки. Несохранённая
 * правка корзины живёт в очереди корзины (`cartSlice`, срок `session`). Сама продажа — в кэше RTK Query (`aqsiActiveSale`): после F5
 * окно читает её с сервера, а не отсюда.
 */
const initialState: SaleIntentState = { seats: {}, resolveDrafts: {} }

/**
 * Ключ кассы работы: `registerKey` — производный ключ кассы из ответа службы (прежнее имя поля — место; ключ
 * намерений и кэша). Запросам служба адресует кассу кабинета — `deviceId` (`SeatWithDevice`, RP-C9).
 */
export interface SeatRef {
  resourceId: string
  registerKey: string
}

/** Касса работы для запросов: ключ намерений и номер кассы кабинета. */
export interface SeatWithDevice extends SeatRef {
  deviceId: number
}

export const seatKey = (ref: SeatRef) => ridKey(ref.resourceId, ref.registerKey.toLowerCase())
export const resolveKey = (resourceId: string, operationId: string) => ridKey(resourceId, operationId.toLowerCase())

/** Позиция в корзине — товар и штрихкод: один товар с разными кодами — разные позиции (код уходит в чек). */
export const lineKey = (line: { productKey: string; barcode: string | null }) => `${line.productKey}|${line.barcode ?? ''}`

const EMPTY_SEAT: SaleSeatIntent = {
  paymentKind: 'Card',
  lookups: [],
  pickerQuery: '',
  pickerPage: 0,
  finishedSaleId: null,
  outside: null,
  searchText: '',
  selectedLineKey: null,
  retry: null,
  park: null,
  delivery: null,
}

export const EMPTY_RESOLVE_DRAFT: ResolveDraft = { outcome: null, reason: '', checked: '', resultId: '', phase: 'editing' }

function seatOf(state: SaleIntentState, ref: SeatRef): SaleSeatIntent {
  const key = seatKey(ref)
  // Массив очереди — свой у каждого места: общий из EMPTY_SEAT immer замораживает, и push в него падает.
  const seat = state.seats[key] ?? { ...EMPTY_SEAT, lookups: [] }
  state.seats[key] = seat
  return seat
}

const saleSlice = createSlice({
  name: 'aqsi/sale',
  initialState,
  reducers: {
    /**
     * Правка корзины человеком. Сам черновик копит и отправляет очередь корзины (`cartSlice` отвечает на это же действие); здесь — то,
     * что правка делает с намерением окна: итог прошлой продажи сходит с экрана, добавленная позиция становится выбранной.
     */
    cartEdited(state, action: PayloadAction<SeatWithDevice & { base: CartBase; edit: CartEdit }>) {
      const seat = seatOf(state, action.payload)
      const { edit } = action.payload
      seat.finishedSaleId = null
      // Полоса правки показывает то, что человек только что добавил (как в старом РМК).
      if (edit.type === 'add') seat.selectedLineKey = lineKey(edit.line)
    },
    /** Новая фискальная смена — новая корзина и новое состояние экрана этой кассы. */
    saleSeatCleared(state, action: PayloadAction<SeatRef>) {
      delete state.seats[seatKey(action.payload)]
    },
    paymentKindChosen(state, action: PayloadAction<SeatRef & { kind: PaymentKind }>) {
      seatOf(state, action.payload).paymentKind = action.payload.kind
    },
    pickerSearchChanged(state, action: PayloadAction<SeatRef & { query: string }>) {
      const seat = seatOf(state, action.payload)
      seat.pickerQuery = action.payload.query.slice(0, 200)
    },
    pickerPageChanged(state, action: PayloadAction<SeatRef & { page: number }>) {
      const page = action.payload.page
      if (Number.isInteger(page) && page >= 0 && page < 100_000) seatOf(state, action.payload).pickerPage = page
    },
    saleFinishedShown(state, action: PayloadAction<SeatRef & { saleId: string }>) {
      const seat = seatOf(state, action.payload)
      seat.finishedSaleId = action.payload.saleId
      seat.outside = null
      seat.retry = null
      seat.park = null
    },
    newSaleStarted(state, action: PayloadAction<SeatRef>) {
      const seat = state.seats[seatKey(action.payload)]
      if (seat !== undefined) seat.finishedSaleId = null
    },

    /** UI-RMK: строка поиска РМК — что набрал человек или ввёл сканер-клавиатура. */
    searchTextChanged(state, action: PayloadAction<SeatRef & { text: string }>) {
      seatOf(state, action.payload).searchText = action.payload.text.slice(0, 128)
    },
    /** Поиск товара (Enter, сканер) поставлен в очередь кассы. */
    lookupQueued(state, action: PayloadAction<SeatRef & { id: string; text: string; command: PendingLookup['command'] }>) {
      const { id, text, command } = action.payload
      seatOf(state, action.payload).lookups.push({ id, text, command, attempt: 0, retryAt: null })
    },
    /** Началась попытка запроса поиска. */
    lookupAttempt(state, action: PayloadAction<SeatRef & { id: string; attempt: number }>) {
      const item = state.seats[seatKey(action.payload)]?.lookups.find((entry) => entry.id === action.payload.id)
      if (item === undefined) return
      item.attempt = action.payload.attempt
      item.retryAt = null
    },
    /** Запрос поиска не получил ответа: следующая попытка в `retryAt`. */
    lookupWaiting(state, action: PayloadAction<SeatRef & { id: string; retryAt: string }>) {
      const item = state.seats[seatKey(action.payload)]?.lookups.find((entry) => entry.id === action.payload.id)
      if (item !== undefined) item.retryAt = action.payload.retryAt
    },
    /** Поиск закончен (ответ получен) или снят кассиром. */
    lookupDone(state, action: PayloadAction<SeatRef & { id: string }>) {
      const seat = state.seats[seatKey(action.payload)]
      if (seat !== undefined) seat.lookups = seat.lookups.filter((entry) => entry.id !== action.payload.id)
    },
    /** UI-RMK: позиция в полосе правки (выбрана в таблице чека). */
    lineSelected(state, action: PayloadAction<SeatRef & { key: string | null }>) {
      seatOf(state, action.payload).selectedLineKey = action.payload.key
    },
    /** Открыть форму «Привязать уже пробитый чек» для продажи, которую видит человек. */
    outsideOpened(state, action: PayloadAction<SeatRef & { saleId: string }>) {
      const seat = seatOf(state, action.payload)
      if (seat.outside?.saleId === action.payload.saleId) return
      seat.outside = { saleId: action.payload.saleId, reason: '', receiptId: '' }
    },
    outsideChanged(state, action: PayloadAction<SeatRef & { reason?: string; receiptId?: string }>) {
      const outside = state.seats[seatKey(action.payload)]?.outside
      if (outside == null) return
      // Пределы договора: причина 1…2000, номер чека ≤ 128.
      if (action.payload.reason !== undefined) outside.reason = action.payload.reason.slice(0, 2000)
      if (action.payload.receiptId !== undefined) outside.receiptId = action.payload.receiptId.slice(0, 128)
    },
    outsideClosed(state, action: PayloadAction<SeatRef>) {
      const seat = state.seats[seatKey(action.payload)]
      if (seat !== undefined) seat.outside = null
    },

    /** Открыть форму «Чека нет — пробить заново» (RP-FB35 K7) для продажи, которую видит человек. */
    retryOpened(state, action: PayloadAction<SeatRef & { saleId: string }>) {
      const seat = seatOf(state, action.payload)
      if (seat.retry?.saleId === action.payload.saleId) return
      seat.retry = { saleId: action.payload.saleId, checked: null, checkedText: '', reason: '' }
    },
    retryChanged(
      state,
      action: PayloadAction<SeatRef & { checked?: ReceiptRetryDraft['checked']; checkedText?: string; reason?: string }>,
    ) {
      const retry = state.seats[seatKey(action.payload)]?.retry
      if (retry == null) return
      if (action.payload.checked !== undefined) retry.checked = action.payload.checked
      // Пределы договора: «что проверено» и причина — до 2000 знаков.
      if (action.payload.checkedText !== undefined) retry.checkedText = action.payload.checkedText.slice(0, 1900)
      if (action.payload.reason !== undefined) retry.reason = action.payload.reason.slice(0, 2000)
    },
    /** Открыть подтверждение «Отложить продажу» (RP-FB35 K9). */
    parkOpened(state, action: PayloadAction<SeatRef & { saleId: string }>) {
      const seat = seatOf(state, action.payload)
      if (seat.park?.saleId === action.payload.saleId) return
      seat.park = { saleId: action.payload.saleId, reason: '' }
    },
    parkReasonChanged(state, action: PayloadAction<SeatRef & { reason: string }>) {
      const park = state.seats[seatKey(action.payload)]?.park
      if (park != null) park.reason = action.payload.reason.slice(0, 2000)
    },
    parkClosed(state, action: PayloadAction<SeatRef>) {
      const seat = state.seats[seatKey(action.payload)]
      if (seat !== undefined) seat.park = null
    },
    /** Найденный товар есть в общем каталоге, но карточки в кассе нет: показать причину и кнопку «Отправить в кассу сейчас». */
    deliveryOffered(state, action: PayloadAction<SeatRef & { offer: DeliveryOffer }>) {
      seatOf(state, action.payload).delivery = action.payload.offer
    },
    /** Ход отправки в кассу; новые слова причины (отказ раздачи) заменяют прежние. */
    deliveryPhaseChanged(state, action: PayloadAction<SeatRef & { phase: DeliveryOffer['phase']; reason?: string }>) {
      const delivery = state.seats[seatKey(action.payload)]?.delivery
      if (delivery == null) return
      delivery.phase = action.payload.phase
      if (action.payload.reason !== undefined) delivery.reason = action.payload.reason
    },
    deliveryDismissed(state, action: PayloadAction<SeatRef>) {
      const seat = state.seats[seatKey(action.payload)]
      if (seat !== undefined) seat.delivery = null
    },
    retryClosed(state, action: PayloadAction<SeatRef>) {
      const seat = state.seats[seatKey(action.payload)]
      if (seat !== undefined) seat.retry = null
    },

    resolveDraftChanged(
      state,
      action: PayloadAction<{ resourceId: string; operationId: string; patch: Partial<Omit<ResolveDraft, 'phase'>> }>,
    ) {
      const key = resolveKey(action.payload.resourceId, action.payload.operationId)
      const draft = state.resolveDrafts[key] ?? { ...EMPTY_RESOLVE_DRAFT }
      // Ушедшую сверку не правим: её исход сначала узнаётся чтением.
      if (draft.phase === 'sending' || draft.phase === 'unknown' || draft.phase === 'done') return
      state.resolveDrafts[key] = {
        ...draft,
        ...action.payload.patch,
        reason: (action.payload.patch.reason ?? draft.reason).slice(0, 2000),
        checked: (action.payload.patch.checked ?? draft.checked).slice(0, 2000),
        resultId: (action.payload.patch.resultId ?? draft.resultId).slice(0, 128),
      }
    },
    resolvePhaseChanged(
      state,
      action: PayloadAction<{ resourceId: string; operationId: string; phase: ResolveDraft['phase'] }>,
    ) {
      const key = resolveKey(action.payload.resourceId, action.payload.operationId)
      const draft = state.resolveDrafts[key] ?? { ...EMPTY_RESOLVE_DRAFT }
      state.resolveDrafts[key] = { ...draft, phase: action.payload.phase }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      dropResource(state.seats, action.payload.resourceId)
      dropResource(state.resolveDrafts, action.payload.resourceId)
    })
    // К-115: Enter (сканер-клавиатура жмёт его сам) освобождает строку поиска в тот же миг, когда код уходит в очередь кассы. Следующий
    // скан печатается в пустую строку и не склеивается с прежним кодом, а ответ на прежний поиск строку уже не трогает.
    builder.addCase(saleSearchSubmitted, (state, action) => {
      seatOf(state, action.payload).searchText = ''
    })
  },
})

export const {
  cartEdited,
  lookupAttempt,
  lookupDone,
  lookupQueued,
  lookupWaiting,
  saleSeatCleared,
  paymentKindChosen,
  pickerSearchChanged,
  pickerPageChanged,
  saleFinishedShown,
  newSaleStarted,
  searchTextChanged,
  lineSelected,
  outsideOpened,
  outsideChanged,
  outsideClosed,
  retryOpened,
  retryChanged,
  retryClosed,
  parkOpened,
  parkReasonChanged,
  parkClosed,
  deliveryOffered,
  deliveryPhaseChanged,
  deliveryDismissed,
  resolveDraftChanged,
  resolvePhaseChanged,
} = saleSlice.actions
export const saleReducer = saleSlice.reducer

export function seatIntentOf(state: SaleIntentState, ref: SeatRef): SaleSeatIntent {
  return state.seats[seatKey(ref)] ?? EMPTY_SEAT
}

export function resolveDraftOf(state: SaleIntentState, resourceId: string, operationId: string): ResolveDraft {
  return state.resolveDrafts[resolveKey(resourceId, operationId)] ?? EMPTY_RESOLVE_DRAFT
}
